import _ from 'lodash';
import React from 'react';
import {connect} from 'react-redux';

import {hasEntityPermission} from 'utils/permissions';

import {
  fetchRoomsAvailability,
  fetchAllRooms,
  selectAllRooms,
  selectFloors,
  selectRoomsAvailability,
  selectRoomsAvailabilityLoading,
  fetchRoomTypes,
  selectRoomTypes,
  fetchFloors,
  fetchRoomTypeFilters,
  selectRoomTypeFilters,
  fetchRoomNeighbours,
  selectRoomNeighbours
} from 'ducks/rooms';
import {composeActions} from 'ducks/common';
import {
  PERMISSIONS,
  requiresPermission,
  selectCurrentUserPermissions
} from 'ducks/auth';
import {openModal, MODALS as BASE_MODALS, isModalOpen} from 'ducks/modals';
import {exportCsvInBackground, selectExporting} from 'ducks/files';
import {exportRoomsAvailabilityCsvInBackground as exportRoomsAvailabilityCsvInBackgroundSdk} from 'sdk/rooms';

import Page from 'components/Page';
import ListContent from 'components/ListContent';
import {Container} from 'containers/types';
import VisibleIf from 'components/VisibleIf';
import SalesNotesModal from 'components/SalesNotesModal';
import {
  default as LoginRequired,
  LoginRequiredProps
} from 'components/LoginRequired';
import Loading from 'components/Loading';
import {
  withPagination,
  PAGINATION,
  WithPaginationProps
} from 'components/withPagination';
import {withFilters, FILTERS, WithFiltersProps} from 'components/withFilters';

import {
  AvailabilityTable,
  AvailabilityFilters,
  SubNavigation
} from './components';

import './styles.css';
import {withSorting, SORTING, WithSortingProps} from 'components/withSorting';

const AVAILABILITY_CSV_FILENAME = 'rooms_availability';

export const initialFilters: {
  search: string;
  roomPurposes: string[];
  roomTypes: string[];
  roomStatuses: string[];
  rooms: number[];
  floors: number[];
  startDate: string | null;
  endDate: string | null;
  dummy: boolean | null;
  booking: number | null;
} = {
  search: '',
  roomTypes: [],
  roomPurposes: [],
  roomStatuses: [],
  rooms: [],
  floors: [],
  startDate: '',
  endDate: '',
  dummy: null,
  booking: null
};

class Availability extends React.Component<
  Container &
    LoginRequiredProps &
    WithPaginationProps &
    WithSortingProps &
    WithFiltersProps<typeof initialFilters> &
    ReturnType<typeof mapStateToProps> &
    typeof mapDispatchToProps
> {
  componentDidMount() {
    const {match: {params: {locationRef}}} = this.props;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_ROOMS_DASHBOARD,
        locationRef
      },
      () => {
        this.fetchRoomsAvailability();
        this.fetchAllRooms();
        this.props.fetchFloors({locationRef});
        this.props.fetchRoomNeighbours({locationRef});
        this.props.fetchRoomTypes({locationRef});
        this.props.fetchRoomTypeFilters({locationRef});
      }
    );
  }

  fetchRoomsAvailability = () =>
    this.props.fetchRoomsAvailability({
      locationRef: this.props.match.params.locationRef
    });

  fetchAllRooms = () =>
    this.props.fetchAllRooms({
      locationRef: this.props.match.params.locationRef
    });

  onPageChange = ({offset, pageSize}) => {
    const {locationRef} = this.props.match.params;

    this.props.onPaginationChange({pageSize, offset}, [
      () => fetchRoomsAvailability({locationRef})
    ]);
  };

  setFilter = (field, value) =>
    this.props.composeActions([
      () => this.props.setFilter(field, value),
      this.fetchRoomsAvailability
    ]);

  clearFilters = () =>
    this.props.composeActions([
      this.props.clearFilters,
      this.fetchRoomsAvailability
    ]);

  onSort = fieldName => {
    const {match: {params: {locationRef}}} = this.props;
    const pagination = this.props.getPagination();

    this.props.onSort(fieldName, [
      () => fetchRoomsAvailability({locationRef}),
      () =>
        this.props.onPaginationChange({
          offset: 0,
          pageSize: pagination.pageSize
        })
    ]);
  };

  checkViewNeighboursPermission = () =>
    this.props.requiresPermission({
      permission: PERMISSIONS.VIEW_NEIGHBOURS_DETAILS,
      locationRef: this.props.match.params.locationRef
    });

  hasViewNeighboursPermission = hasEntityPermission({
    userPermissions: this.props.userPermissions,
    permission: PERMISSIONS.VIEW_NEIGHBOURS_DETAILS,
    locationRef: this.props.match.params.locationRef
  });

  fetchRoomsAvailabilityExtraActions = () => {
    const {match: {params: {locationRef}}} = this.props;

    return [() => fetchRoomsAvailability({locationRef})];
  };

  openSalesNotesModal = ({relatedObjectId}: any) => {
    const {match: {params: {locationRef}}} = this.props;

    this.props.openModal(BASE_MODALS.SALES_NOTES, {
      relatedObjectId,
      locationRef,
      refetchExtraAction: () => []
    });
  };

  exportRoomsAvailabilityCsvInBackground = () => {
    const {locationRef} = this.props.match.params;

    this.props.requiresPermission(
      {
        permission: PERMISSIONS.VIEW_CSV_EXPORT_FILE
      },
      () =>
        this.props.exportCsvInBackground({
          exportData: {
            locationRef,
            filters: this.props.getFilters(),
            sort: this.props.getSort()
          },
          exportSdk: exportRoomsAvailabilityCsvInBackgroundSdk,
          fileName: AVAILABILITY_CSV_FILENAME
        })
    );
  };

  render() {
    const {
      match: {params: {locationRef}},
      roomsAvailabilityLoading,
      roomsAvailability,
      floors,
      allRooms,
      roomTypes,
      roomTypeFilters,
      roomIdToNeighbourRoom,
      modals: {isNotesModalOpened}
    } = this.props;

    return (
      <Page activeTab="availability" locationRef={locationRef}>
        <VisibleIf
          permission={PERMISSIONS.VIEW_ROOMS_DASHBOARD}
          locationRef={locationRef}>
          <ListContent
            className="Availability"
            filters={this.props.getFilters()}
            initialFilters={initialFilters}
            setFilter={this.setFilter}
            navigationExtraElement={SubNavigation}>
            <ListContent.Content>
              {roomsAvailabilityLoading && <Loading />}
              {!roomsAvailabilityLoading && (
                <AvailabilityTable
                  roomsAvailability={roomsAvailability}
                  roomIdToNeighbourRoom={roomIdToNeighbourRoom}
                  checkViewNeighboursPermission={
                    this.checkViewNeighboursPermission
                  }
                  sort={this.props.getSort()}
                  sortBy={this.onSort}
                  hasViewNeighboursPermission={this.hasViewNeighboursPermission}
                  locationRef={locationRef}
                  pagination={this.props.getPagination()}
                  onPageChange={this.onPageChange}
                  openSalesNotesModal={this.openSalesNotesModal}
                  exportRoomsAvailabilityCsvInBackground={
                    this.exportRoomsAvailabilityCsvInBackground
                  }
                  exportingRoomsAvailabilityCsv={
                    this.props.exportingRoomsAvailabilityCsv
                  }
                />
              )}
            </ListContent.Content>
            <ListContent.Filters>
              <AvailabilityFilters
                locationRef={locationRef}
                setFilter={this.setFilter}
                getFilters={this.props.getFilters}
                clearFilters={this.clearFilters}
                roomTypeFilters={roomTypeFilters}
                roomTypes={roomTypes}
                rooms={allRooms}
                floors={floors}
              />
            </ListContent.Filters>
          </ListContent>
        </VisibleIf>

        {isNotesModalOpened && <SalesNotesModal />}
      </Page>
    );
  }
}

const mapStateToProps = state => {
  return {
    roomsAvailabilityLoading: selectRoomsAvailabilityLoading(state),
    roomsAvailability: selectRoomsAvailability(state),
    floors: selectFloors(state),
    allRooms: selectAllRooms(state),
    roomTypes: selectRoomTypes(state),
    roomTypeFilters: selectRoomTypeFilters(state),
    roomIdToNeighbourRoom: selectRoomNeighbours(state),
    userPermissions: selectCurrentUserPermissions(state),
    exportingRoomsAvailabilityCsv: selectExporting(
      state,
      AVAILABILITY_CSV_FILENAME
    ),
    modals: {
      isNotesModalOpened: isModalOpen(state, BASE_MODALS.SALES_NOTES)
    }
  };
};

const mapDispatchToProps = {
  fetchRoomsAvailability,
  fetchAllRooms,
  fetchRoomTypes,
  fetchFloors,
  composeActions,
  fetchRoomTypeFilters,
  fetchRoomNeighbours,
  requiresPermission,
  openModal,
  exportCsvInBackground
};

const authenticatedComponent = LoginRequired(Availability);

const componentWithSorting = withSorting(
  authenticatedComponent,
  SORTING.AVAILABILITY_TABLE,
  {
    saveToUrl: true
  }
);

const componentWithSortingAndPagination = withPagination(
  componentWithSorting,
  PAGINATION.AVAILABILITY,
  {
    saveToUrl: true,
    pageSizes: [20, 50, 100],
    pageSize: 20
  }
);

const componentWithSortingPaginationAndFilters = withFilters(
  componentWithSortingAndPagination,
  FILTERS.AVAILABILITY,
  {initialFilters, saveToUrl: true}
);

export default connect(mapStateToProps, mapDispatchToProps)(
  componentWithSortingPaginationAndFilters
);
